'use client';

import React, { useState, useEffect, useCallback, useRef, Suspense } from 'react';

import Header from '@/components/Header';
import Footer from '@/components/Footer';

type Brand = 'All' | 'Mercedes-Benz' | 'BMW' | 'Audi' | 'Volkswagen' | 'Other';

interface GalleryItem {
  id: number;
  caption: string;
  brand: Exclude<Brand, 'All'>;
  beforeBg: string;
  afterBg: string;
}

const galleryItems: GalleryItem[] = [
  { id: 1, caption: 'BMW 3 Series – Rear-end collision repair', brand: 'BMW', beforeBg: '#1a1010', afterBg: '#0f1a0f' },
  { id: 2, caption: 'Mercedes C-Class – Side impact restoration', brand: 'Mercedes-Benz', beforeBg: '#1a1010', afterBg: '#0f1a0f' },
  { id: 3, caption: 'Audi A4 – Front-end damage', brand: 'Audi', beforeBg: '#1a1010', afterBg: '#0f1a0f' },
  { id: 4, caption: 'Mercedes E-Class – Airbag deployment repair', brand: 'Mercedes-Benz', beforeBg: '#1a1010', afterBg: '#0f1a0f' },
  { id: 5, caption: 'BMW X5 – Structural alignment', brand: 'BMW', beforeBg: '#1a1010', afterBg: '#0f1a0f' },
  { id: 6, caption: 'Volkswagen Golf – Panel repair', brand: 'Volkswagen', beforeBg: '#1a1010', afterBg: '#0f1a0f' },
  { id: 7, caption: 'Mercedes GLC – Bumper replacement and paint', brand: 'Mercedes-Benz', beforeBg: '#1a1010', afterBg: '#0f1a0f' },
  { id: 8, caption: 'BMW 5 Series – Door panel restoration', brand: 'BMW', beforeBg: '#1a1010', afterBg: '#0f1a0f' },
  { id: 9, caption: 'Audi A6 – Rear quarter panel repair', brand: 'Audi', beforeBg: '#1a1010', afterBg: '#0f1a0f' },
  { id: 10, caption: 'Volkswagen Passat – Front-end collision repair', brand: 'Volkswagen', beforeBg: '#1a1010', afterBg: '#0f1a0f' },
  { id: 11, caption: 'BMW M3 – Full respray and paint correction', brand: 'BMW', beforeBg: '#1a1010', afterBg: '#0f1a0f' },
  { id: 12, caption: 'Classic restoration – Panel beating and paint', brand: 'Other', beforeBg: '#1a1010', afterBg: '#0f1a0f' },
];

const brands: Brand[] = ['All', 'Mercedes-Benz', 'BMW', 'Audi', 'Volkswagen', 'Other'];

function CarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 80 50" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect x="8" y="22" width="64" height="18" rx="3" fill="currentColor" opacity="0.15"/>
      <path d="M14 22 L22 10 L58 10 L66 22" stroke="currentColor" strokeWidth="2" fill="none" opacity="0.4"/>
      <circle cx="22" cy="40" r="7" stroke="currentColor" strokeWidth="2" fill="none" opacity="0.5"/>
      <circle cx="58" cy="40" r="7" stroke="currentColor" strokeWidth="2" fill="none" opacity="0.5"/>
      <rect x="30" y="12" width="20" height="10" rx="1" fill="currentColor" opacity="0.2"/>
    </svg>
  );
}

function BeforePlaceholder() {
  return (
    <div className="relative w-full h-full flex flex-col items-center justify-center bg-[#1a0a0a] border border-[#CC1A1A]/20" aria-label="Before repair image placeholder">
      <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'linear-gradient(rgba(204,26,26,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(204,26,26,0.5) 1px, transparent 1px)', backgroundSize: '20px 20px' }} aria-hidden="true" />
      <CarIcon className="w-16 h-10 text-[#CC1A1A] mb-2" />
      <span className="font-dm-sans text-[#CC1A1A]/50 text-xs tracking-widest uppercase" aria-hidden="true">Damaged</span>
    </div>
  );
}

function AfterPlaceholder() {
  return (
    <div className="relative w-full h-full flex flex-col items-center justify-center bg-[#0a1a0a] border border-[#22c55e]/20" aria-label="After repair image placeholder">
      <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'linear-gradient(rgba(34,197,94,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(34,197,94,0.5) 1px, transparent 1px)', backgroundSize: '20px 20px' }} aria-hidden="true" />
      <CarIcon className="w-16 h-10 text-[#22c55e] mb-2" />
      <span className="font-dm-sans text-[#22c55e]/50 text-xs tracking-widest uppercase" aria-hidden="true">Restored</span>
    </div>
  );
}

function GalleryGridSkeleton() {
  return (
    <div
      className="w-full py-24 bg-[#111111]"
      aria-hidden="true"
      style={{
        background: 'linear-gradient(90deg, #111111 25%, #1a1a1a 50%, #111111 75%)',
        backgroundSize: '200% 100%',
        animation: 'shimmer 1.5s infinite linear',
      }}
    />
  );
}

function GalleryCard({ item, onClick, cardRef }: { item: GalleryItem; onClick: () => void; cardRef?: React.Ref<HTMLDivElement> }) {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div
      ref={cardRef}
      className="group bg-[#111111] border border-white/[0.08] hover:border-[#CC1A1A]/40 transition-all duration-300 cursor-pointer overflow-hidden"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={handleKeyDown}
      aria-label={`View ${item.caption} before and after repair images`}
    >
      {/* Image pair */}
      <div className="grid grid-cols-2 h-44 relative">
        {/* Before */}
        <div className="relative overflow-hidden">
          <BeforePlaceholder />
          <span className="absolute top-2 left-2 px-2 py-0.5 bg-[#CC1A1A] font-dm-sans text-white text-[10px] font-bold tracking-widest uppercase z-10" aria-hidden="true">
            BEFORE
          </span>
        </div>
        {/* Divider */}
        <div className="absolute left-1/2 top-0 bottom-0 w-px bg-white/10 z-10" aria-hidden="true" />
        {/* After */}
        <div className="relative overflow-hidden">
          <AfterPlaceholder />
          <span className="absolute top-2 left-2 px-2 py-0.5 bg-[#22c55e] font-dm-sans text-white text-[10px] font-bold tracking-widest uppercase z-10" aria-hidden="true">
            AFTER
          </span>
        </div>
        {/* Hover overlay */}
        <div className="absolute inset-0 bg-[#CC1A1A]/0 group-hover:bg-[#CC1A1A]/5 transition-all duration-300 z-20 flex items-center justify-center" aria-hidden="true">
          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" aria-hidden="true">
              <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/>
            </svg>
          </div>
        </div>
      </div>

      {/* Card footer */}
      <div className="px-4 py-3 flex items-center justify-between gap-2">
        <p className="font-dm-sans text-white/70 text-sm leading-snug flex-1">{item.caption}</p>
        <span className="flex-shrink-0 px-2 py-0.5 border border-[#CC1A1A]/30 bg-[#CC1A1A]/10 font-dm-sans text-[#CC1A1A] text-[10px] font-bold tracking-wider uppercase">
          {item.brand}
        </span>
      </div>
    </div>
  );
}

function Lightbox({ item, onClose, triggerRef }: { item: GalleryItem; onClose: () => void; triggerRef?: React.RefObject<HTMLDivElement | null> }) {
  const [view, setView] = useState<'before' | 'after'>('before');
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const lightboxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    closeButtonRef.current?.focus();
  }, []);

  useEffect(() => {
    return () => {
      triggerRef?.current?.focus();
    };
  }, [triggerRef]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key === 'Tab' && lightboxRef.current) {
        const focusableSelectors = 'button:not([disabled]), [tabindex]:not([tabIndex="-1"])';
        const focusable = Array.from(lightboxRef.current.querySelectorAll<HTMLElement>(focusableSelectors));
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        ref={lightboxRef}
        role="dialog"
        aria-modal="true"
        aria-label="Repair image details"
        className="relative w-full max-w-2xl bg-[#111111] border border-white/[0.1] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          ref={closeButtonRef}
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-[#CC1A1A] flex items-center justify-center hover:bg-[#E02020] transition-colors min-w-[44px] min-h-[44px]"
          aria-label="Close lightbox"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" aria-hidden="true">
            <path d="M18 6L6 18M6 6l12 12"/>
          </svg>
        </button>

        <div className="h-72 sm:h-96 relative">
          {view === 'before' ? (
            <div aria-label="Before repair">
              <BeforePlaceholder />
            </div>
          ) : (
            <div aria-label="After repair">
              <AfterPlaceholder />
            </div>
          )}
          <span
            className={`absolute top-4 left-4 px-3 py-1 font-dm-sans text-white text-xs font-bold tracking-widest uppercase ${
              view === 'before' ? 'bg-[#CC1A1A]' : 'bg-[#22c55e]'
            }`}
            aria-hidden="true"
          >
            {view === 'before' ? 'BEFORE' : 'AFTER'}
          </span>
        </div>

        <div className="px-6 py-5">
          <div className="flex items-center gap-1 p-1 bg-[#0a0a0a] border border-white/[0.08] w-fit mb-4" role="group" aria-label="Toggle before and after view">
            <button
              onClick={() => setView('before')}
              className={`px-5 py-2 font-dm-sans text-xs font-bold tracking-widest uppercase transition-all duration-200 min-h-[44px] ${
                view === 'before'
                  ? 'bg-[#CC1A1A] text-white' : 'text-white/40 hover:text-white/70'
              }`}
              aria-pressed={view === 'before'}
            >
              Before
            </button>
            <button
              onClick={() => setView('after')}
              className={`px-5 py-2 font-dm-sans text-xs font-bold tracking-widest uppercase transition-all duration-200 min-h-[44px] ${
                view === 'after' ? 'bg-[#22c55e] text-white' : 'text-white/40 hover:text-white/70'
              }`}
              aria-pressed={view === 'after'}
            >
              After
            </button>
          </div>

          <p className="font-fraunces text-white text-lg font-light mb-2">{item.caption}</p>
          <span className="inline-block px-2 py-0.5 border border-[#CC1A1A]/30 bg-[#CC1A1A]/10 font-dm-sans text-[#CC1A1A] text-[10px] font-bold tracking-wider uppercase">
            {item.brand}
          </span>
          <p className="font-dm-sans text-white/30 text-xs mt-3" aria-hidden="true">Press ESC to close</p>
        </div>
      </div>
    </div>
  );
}

function GalleryContent() {
  const [activeFilter, setActiveFilter] = useState<Brand>('All');
  const [selectedItem, setSelectedItem] = useState<GalleryItem | null>(null);
  const cardRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const lastOpenedId = useRef<number | null>(null);

  const filtered = activeFilter === 'All'
    ? galleryItems
    : galleryItems.filter((item) => item.brand === activeFilter);

  const handleOpen = useCallback((item: GalleryItem) => {
    lastOpenedId.current = item.id;
    setSelectedItem(item);
  }, []);

  const handleClose = useCallback(() => {
    setSelectedItem(null);
  }, []);

  const triggerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (selectedItem) {
      triggerRef.current = cardRefs.current.get(selectedItem.id) ?? null;
    }
  }, [selectedItem]);

  return (
    <>
      {/* Filter bar */}
      <div className="flex flex-wrap gap-2 mb-8" role="group" aria-label="Filter gallery by vehicle brand">
        {brands.map((brand) => (
          <button
            key={brand}
            onClick={() => setActiveFilter(brand)}
            className={`px-4 py-2 font-dm-sans text-xs font-bold tracking-widest uppercase transition-all duration-200 border min-h-[44px] ${
              activeFilter === brand
                ? 'bg-[#CC1A1A] border-[#CC1A1A] text-white'
                : 'border-white/[0.12] text-white/50 hover:border-[#CC1A1A]/50 hover:text-white/80'
            }`}
            aria-pressed={activeFilter === brand}
          >
            {brand}
          </button>
        ))}
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((item) => (
          <GalleryCard
            key={item.id}
            item={item}
            onClick={() => handleOpen(item)}
            cardRef={(el) => {
              if (el) cardRefs.current.set(item.id, el);
              else cardRefs.current.delete(item.id);
            }}
          />
        ))}
      </div>

      {/* Lightbox — only mounted when open */}
      {selectedItem && (
        <Lightbox
          item={selectedItem}
          onClose={handleClose}
          triggerRef={triggerRef as React.RefObject<HTMLDivElement | null>}
        />
      )}
    </>
  );
}

export default function GalleryPage() {
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <Header />
      <main id="main-content">
        {/* Hero */}
        <section className="pt-32 pb-16 px-6 relative overflow-hidden" aria-labelledby="gallery-hero-heading">
          <div
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage:
                'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
              backgroundSize: '60px 60px',
            }}
            aria-hidden="true"
          />
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-[#CC1A1A]/10 blur-[120px] rounded-full" aria-hidden="true" />

          <div className="max-w-4xl mx-auto text-center relative z-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 border border-[#CC1A1A]/30 bg-[#CC1A1A]/10 mb-8" aria-hidden="true">
              <span className="w-1.5 h-1.5 rounded-full bg-[#CC1A1A] animate-pulse" />
              <span className="font-dm-sans text-[#CC1A1A] text-xs font-bold tracking-[0.2em] uppercase">OUR WORK</span>
            </div>
            <h1 id="gallery-hero-heading" className="font-fraunces text-5xl md:text-6xl font-light text-white leading-tight mb-6">
              Before &amp; After
              <br />
              <span className="text-[#CC1A1A]">Repair Gallery</span>
            </h1>
            <p className="font-dm-sans text-white/50 text-lg md:text-xl leading-relaxed max-w-2xl mx-auto">
              Real repairs. Real results. Browse our work across all makes and models.
            </p>
          </div>
        </section>

        {/* Gallery content — lazy loaded */}
        <section className="pb-24 px-6" aria-label="Gallery grid">
          <div className="max-w-7xl mx-auto">
            <Suspense fallback={<GalleryGridSkeleton />}>
              <GalleryContent />
            </Suspense>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
